/**
 * Structured inventory editor — desktop table + mobile cards.
 * Edits individual rows (name, qty, details, tags) with add/edit/delete.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  INVENTORY_TAG_OPTIONS,
  createEmptyInventoryRow,
  normalizeInventoryRow,
} from "./structuredInventoryHelpers.js";

/**
 * @param {{
 *   rows?: Array,
 *   onChange?: (rows: Array) => void,
 *   disabled?: boolean,
 * }} props
 */
const StructuredInventoryEditor = ({
  rows = [],
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);

  const emit = (next) => {
    onChange?.(next.map((r) => normalizeInventoryRow(r)));
  };

  const startEdit = (row) => {
    if (disabled) return;
    setEditingId(row.id);
    setDraft(normalizeInventoryRow(row));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const commitEdit = () => {
    if (!draft) return;
    const normalized = normalizeInventoryRow(draft);
    if (!normalized.name) return;
    const exists = rows.some((r) => r.id === normalized.id);
    const next = exists
      ? rows.map((r) => (r.id === normalized.id ? normalized : r))
      : [...rows, normalized];
    emit(next);
    cancelEdit();
  };

  const startAdd = () => {
    if (disabled) return;
    const empty = createEmptyInventoryRow();
    setEditingId(empty.id);
    setDraft(empty);
  };

  const removeRow = (id) => {
    if (disabled) return;
    if (editingId === id) cancelEdit();
    emit(rows.filter((r) => r.id !== id));
  };

  const updateQty = (id, quantity) => {
    if (disabled) return;
    const qty = Math.max(1, Number(quantity) || 1);
    if (editingId === id && draft) {
      setDraft((prev) => ({ ...prev, quantity: qty }));
      return;
    }
    emit(
      rows.map((r) =>
        r.id === id ? normalizeInventoryRow({ ...r, quantity: qty }) : r
      )
    );
  };

  const toggleTag = (tag) => {
    if (!draft || disabled) return;
    setDraft((prev) => {
      const has = prev.tags.includes(tag);
      return {
        ...prev,
        tags: has ? prev.tags.filter((t) => t !== tag) : [...prev.tags, tag],
      };
    });
  };

  const editingForm = draft ? (
    <Stack
      spacing={1.5}
      data-testid="inventory-row-editor"
      sx={{
        p: 1.5,
        border: 1,
        borderColor: "primary.light",
        borderRadius: 1,
        bgcolor: "action.hover",
      }}
    >
      <TextField
        label="Item name"
        value={draft.name}
        onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
        size="small"
        fullWidth
        required
        disabled={disabled}
        autoFocus
        inputProps={{ "data-testid": "inventory-name-input" }}
      />
      <Stack direction="row" spacing={1}>
        <TextField
          label="Qty"
          type="number"
          value={draft.quantity}
          onChange={(e) =>
            setDraft((p) => ({
              ...p,
              quantity: Math.max(1, parseInt(e.target.value, 10) || 1),
            }))
          }
          size="small"
          sx={{ width: 96 }}
          disabled={disabled}
          inputProps={{ min: 1, "data-testid": "inventory-qty-input" }}
        />
        <TextField
          label="Details"
          value={draft.details}
          onChange={(e) => setDraft((p) => ({ ...p, details: e.target.value }))}
          size="small"
          fullWidth
          disabled={disabled}
          inputProps={{ "data-testid": "inventory-details-input" }}
        />
      </Stack>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {INVENTORY_TAG_OPTIONS.map((tag) => {
          const selected = draft.tags.includes(tag);
          return (
            <Chip
              key={tag}
              label={tag}
              size="small"
              color={selected ? "primary" : "default"}
              variant={selected ? "filled" : "outlined"}
              onClick={() => toggleTag(tag)}
              disabled={disabled}
              data-testid={`inventory-tag-${tag.toLowerCase()}`}
            />
          );
        })}
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          size="small"
          startIcon={<CloseIcon />}
          onClick={cancelEdit}
          disabled={disabled}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<CheckIcon />}
          onClick={commitEdit}
          disabled={disabled || !String(draft.name || "").trim()}
          data-testid="inventory-save-row"
        >
          Save item
        </Button>
      </Stack>
    </Stack>
  ) : null;

  const isAddingNew =
    draft && !rows.some((r) => r.id === draft.id) ? editingForm : null;

  const mobileList = (
    <Stack spacing={1.5} data-testid="inventory-mobile-list">
      {rows.map((row) => {
        if (editingId === row.id && draft) {
          return <Box key={row.id}>{editingForm}</Box>;
        }
        return (
          <Box
            key={row.id}
            data-testid={`inventory-card-${row.id}`}
            sx={{
              p: 1.5,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.name}
                </Typography>
                {row.details ? (
                  <Typography variant="body2" color="text.secondary">
                    {row.details}
                  </Typography>
                ) : null}
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 0.75 }}
                >
                  <Chip size="small" label={`Qty ${row.quantity}`} />
                  {(row.tags || []).map((tag) => (
                    <Chip key={tag} size="small" label={tag} color="warning" variant="outlined" />
                  ))}
                </Stack>
              </Box>
              <Stack direction="row" spacing={0}>
                <IconButton
                  size="small"
                  aria-label={`Edit ${row.name}`}
                  onClick={() => startEdit(row)}
                  disabled={disabled || Boolean(editingId)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`Delete ${row.name}`}
                  onClick={() => removeRow(row.id)}
                  disabled={disabled}
                  color="error"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
            <TextField
              label="Qty"
              type="number"
              size="small"
              value={row.quantity}
              onChange={(e) => updateQty(row.id, e.target.value)}
              disabled={disabled || Boolean(editingId)}
              sx={{ mt: 1, width: 100 }}
              inputProps={{ min: 1, "data-testid": `inventory-qty-${row.id}` }}
            />
          </Box>
        );
      })}
      {isAddingNew}
    </Stack>
  );

  const desktopTable = (
    <TableContainer
      data-testid="inventory-desktop-table"
      sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell width={88}>Qty</TableCell>
            <TableCell>Details</TableCell>
            <TableCell>Tags</TableCell>
            <TableCell align="right" width={96}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            if (editingId === row.id && draft) {
              return (
                <TableRow key={row.id}>
                  <TableCell colSpan={5}>{editingForm}</TableCell>
                </TableRow>
              );
            }
            return (
              <TableRow key={row.id} data-testid={`inventory-row-${row.id}`}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={row.quantity}
                    onChange={(e) => updateQty(row.id, e.target.value)}
                    disabled={disabled || Boolean(editingId)}
                    inputProps={{
                      min: 1,
                      "data-testid": `inventory-qty-${row.id}`,
                      style: { width: 56 },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {row.details || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(row.tags || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      row.tags.map((tag) => (
                        <Chip key={tag} size="small" label={tag} variant="outlined" />
                      ))
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label={`Edit ${row.name}`}
                    onClick={() => startEdit(row)}
                    disabled={disabled || Boolean(editingId)}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Delete ${row.name}`}
                    onClick={() => removeRow(row.id)}
                    disabled={disabled}
                    color="error"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {isAddingNew ? (
            <TableRow>
              <TableCell colSpan={5}>{isAddingNew}</TableCell>
            </TableRow>
          ) : null}
          {rows.length === 0 && !draft ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  No inventory items yet. Add items extracted from the message or
                  enter them manually.
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box data-testid="structured-inventory-editor">
      {isMobile ? mobileList : desktopTable}
      {!draft ? (
        <Button
          startIcon={<AddIcon />}
          onClick={startAdd}
          disabled={disabled}
          sx={{ mt: 1.5 }}
          data-testid="inventory-add-item"
        >
          Add item
        </Button>
      ) : null}
    </Box>
  );
};

export default StructuredInventoryEditor;
